import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, EditOutlined, InfoCircleOutlined, AuditOutlined } from "@ant-design/icons";
import {
    Button, Card, Col, Descriptions, Modal, Row, Space, Tag, Typography, notification, Select, Alert, Divider, Tooltip
} from "antd";
import React, { useState, useEffect } from "react";
import dayjs from 'dayjs';
import Swal from 'sweetalert2';

const { Title, Text } = Typography;

const ExamResultModal = ({ visible, applicant, onClose, onStatusUpdate }) => {
    const [updateLoading, setUpdateLoading] = useState(false);
    const [editedStrand, setEditedStrand] = useState("");
    const [targetStatus, setTargetStatus] = useState(null);

    const ACADEMIC_STRAND_OPTIONS = [
        { value: "STEM", label: "STEM" },
        { value: "HUMSS", label: "HUMSS" },
        { value: "ABM", label: "ABM" },
        { value: "TVL", label: "TVL" },
    ];

    useEffect(() => {
        if (visible && applicant) {
            setEditedStrand(applicant.academicStrand || "");
            setUpdateLoading(false);
            setTargetStatus(null);
        }
    }, [visible, applicant]);

    if (!applicant) {
        return null;
    }

    const renderStatusTag = (status, defaultText = "Pending") => {
        if (!status) return <Tag>{defaultText}</Tag>;
        const lowerStatus = String(status).toLowerCase();
        let color = 'default';
        if (['complete', 'paid', 'passed', 'approved', 'verified', 'active'].includes(lowerStatus)) color = 'green';
        else if (['submitted', 'pending verification', 'scheduled'].includes(lowerStatus)) color = 'blue';
        else if (['incomplete', 'required', 'pending', 'on-going', 'pending validation'].includes(lowerStatus)) color = 'orange';
        else if (['rejected', 'inactive', 'dropped'].includes(lowerStatus)) color = 'red';
        return <Tag color={color}>{status}</Tag>;
    };

    const showConfirmAction = async (newStatus) => {
        if (newStatus === "Approved" && !editedStrand) {
            notification.warning({ message: "Strand Not Selected", description: "Please assign an academic strand before approving." });
            return;
        }

        let confirmConfig = {
            title: `Confirm Action: ${newStatus}`,
            html: `<p>You are about to mark the result as <strong>${newStatus}</strong>.</p>${newStatus === "Approved" ? `<p>Assigned Strand: <strong>${editedStrand}</strong></p>` : ''}<p style="color: #dc3545; font-weight: bold; margin-top: 12px;">This will notify the applicant. Proceed?</p>`,
            icon: newStatus === "Rejected" ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: `Yes, ${newStatus}`,
            cancelButtonText: 'Cancel',
            confirmButtonColor: newStatus === "Rejected" ? '#dc3545' : '#52c41a',
        };

        if (newStatus === "Rejected") {
            confirmConfig.input = 'textarea';
            confirmConfig.inputPlaceholder = 'Reason for rejection...';
            confirmConfig.preConfirm = (reason) => {
                if (!reason || !reason.trim()) {
                    Swal.showValidationMessage('A rejection reason is required.');
                    return false;
                }
                return reason;
            };
        }

        const result = await Swal.fire(confirmConfig);
        if (result.isConfirmed) {
            const reason = newStatus === "Rejected" ? result.value : null;
            await handleActualAction(newStatus, reason);
        }
    };
    
    const handleActualAction = async (newStatus, reason = null) => {
        setTargetStatus(newStatus);
        setUpdateLoading(true);
        try {
            // The parent component is responsible for formatting the data for the API
            await onStatusUpdate(applicant, newStatus, {
                academicStrand: editedStrand,
                rejectionReason: reason
            });
            notification.success({
                message: `Result ${newStatus}`,
                description: `The applicant's result has been updated.`,
            });
            setTimeout(onClose, 500);
        } catch (error) {
            notification.error({
                message: "Action Failed",
                description: error.message || "Could not update status.",
            });
        } finally {
            setUpdateLoading(false);
            setTargetStatus(null);
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return "N/A";
        return dayjs(`1970-01-01T${timeString}`).format('hh:mm A');
    };

    const formatDate = (date, format = 'MMMM D, YYYY') => dayjs(date).isValid() ? dayjs(date).format(format) : "N/A";

    // --- LOGIC FOR BUTTONS AND ALERTS ---
    const isAlreadyProcessed = ['Approved', 'Rejected'].includes(applicant.approvedExamInterviewResult);
    // This check is now reliable because the backend provides the examFeeValidatedBy field.
    const isPaymentValidated = !!applicant.examFeeValidatedBy;

    const approveButtonDisabled = isAlreadyProcessed || !editedStrand || !isPaymentValidated || updateLoading;
    const rejectButtonDisabled = isAlreadyProcessed || updateLoading;

    return (
        <Modal
            title={<><InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} /> Review Exam/Interview Result</>}
            open={visible}
            onCancel={onClose}
            destroyOnClose={true}
            width={750}
            centered
            footer={
                <Space>
                    <Button
                        key="reject"
                        danger
                        icon={updateLoading && targetStatus === "Rejected" ? <LoadingOutlined /> : <CloseCircleOutlined />}
                        onClick={() => showConfirmAction("Rejected")}
                        loading={updateLoading && targetStatus === "Rejected"}
                        disabled={rejectButtonDisabled}
                    >
                        Reject Result
                    </Button>
                    <Tooltip title={!isPaymentValidated ? "Cannot approve: Exam fee payment has not been validated by Accounting." : ""}>
                        <Button
                            key="approve"
                            type="primary"
                            icon={updateLoading && targetStatus === "Approved" ? <LoadingOutlined /> : <CheckCircleOutlined />}
                            onClick={() => showConfirmAction("Approved")}
                            loading={updateLoading && targetStatus === "Approved"}
                            disabled={approveButtonDisabled}
                            style={{ backgroundColor: approveButtonDisabled ? undefined : "#52c41a", borderColor: approveButtonDisabled ? undefined : "#52c41a" }}
                        >
                            Approve Result
                        </Button>
                    </Tooltip>
                    <Button key="close" onClick={onClose} disabled={updateLoading}>
                        Close
                    </Button>
                </Space>
            }
        >
            <Alert message="Review the applicant's details, assign a strand, and then approve or reject their exam result. This action is final and will notify the applicant." type="info" showIcon style={{ marginBottom: 16 }} />
            
            {!isPaymentValidated && !isAlreadyProcessed && (
                 <Alert
                    message="Payment Pending Validation"
                    description="The 'Approve Result' button is disabled because the applicant's exam fee has not been validated by the accounting department."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Card bordered={false} style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={10}>
                        <Title level={5} style={{ margin: 0 }}>{`${applicant.firstName} ${applicant.lastName}`}</Title>
                        <Text type="secondary">Applicant ID: {applicant.applicantID}</Text>
                    </Col>
                    <Col xs={24} md={14} style={{ textAlign: 'right' }}>
                        <Tag color="cyan">Level: {applicant.academicLevel}</Tag>
                        <Tag color="purple" style={{ marginLeft: 5 }}>Year: {applicant.academicYear}</Tag>
                    </Col>
                </Row>
            </Card>

            <Divider style={{ marginTop: 0, marginBottom: 16 }} />

            <Descriptions title={<><InfoCircleOutlined /> Exam & Interview Summary</>} bordered size="small" column={1} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Exam Schedule Status">{renderStatusTag(applicant.admissionExamDetailsStatus)}</Descriptions.Item>
                <Descriptions.Item label="Exam Date">{formatDate(applicant.approvedExamDate)}</Descriptions.Item>
                <Descriptions.Item label="Exam Time">{formatTime(applicant.approvedExamTime)}</Descriptions.Item>
                <Descriptions.Item label="Exam Room">{applicant.approvedExamRoom || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Exam Result">{renderStatusTag(applicant.approvedExamInterviewResult)}</Descriptions.Item>
            </Descriptions>

            <Descriptions title={<><EditOutlined /> Academic Assignment</>} bordered size="middle" column={1} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Assign/Confirm Strand">
                    <Select
                        value={editedStrand}
                        onChange={setEditedStrand}
                        options={ACADEMIC_STRAND_OPTIONS}
                        style={{ width: '100%' }}
                        placeholder="Select academic strand (Required for Approval)"
                        disabled={isAlreadyProcessed || updateLoading}
                    />
                </Descriptions.Item>
            </Descriptions>
            
            <Descriptions title={<><AuditOutlined /> Payment Validation</>} bordered size="small" column={1}>
                {/* THIS IS WHERE THE DATA IS DISPLAYED */}
                <Descriptions.Item label="Exam Fee Status">{renderStatusTag(applicant.approvedExamFeeStatus, "Pending Payment")}</Descriptions.Item>
                <Descriptions.Item label="Validated By"><Text type="secondary">{applicant.examFeeValidatedBy || "N/A"}</Text></Descriptions.Item>
                <Descriptions.Item label="Validation Date"><Text type="secondary">{formatDate(applicant.examFeeValidationDate, 'YYYY-MM-DD hh:mm A')}</Text></Descriptions.Item>
            </Descriptions>
        </Modal>
    );
};

export default ExamResultModal;